/**
 * CarrierLogo — presentational carrier logo (P2 Placement Slice 7). Renders the company_master
 * logo_url as a lazy <img>; on a null url or an onError it falls back to a rounded initials box
 * (first two letters of the name, or '?'). 16px for chips (sm), 40px for tiles (md).
 */

import { useState } from 'react';
import { cn } from '../../lib/cn';

function initials(name: string | null): string {
  if (!name) return '?';
  const trimmed = name.trim();
  if (trimmed.length === 0) return '?';
  return trimmed.slice(0, 2).toUpperCase();
}

export function CarrierLogo({ name, logoUrl, size }: { name: string | null; logoUrl: string | null; size: 'sm' | 'md' }) {
  const [failed, setFailed] = useState(false);
  const px = size === 'sm' ? 'h-4 w-4 text-[8px]' : 'h-10 w-10 text-xs';
  const showFallback = logoUrl === null || failed;

  if (showFallback) {
    return (
      <span
        className={cn('inline-flex shrink-0 items-center justify-center rounded-md border border-border bg-muted font-medium text-muted-foreground', px)}
        data-testid="carrier-logo"
        data-fallback="true"
        aria-hidden
      >
        {initials(name)}
      </span>
    );
  }

  return (
    <img
      src={logoUrl}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className={cn('shrink-0 rounded-md border border-border bg-background object-contain', size === 'sm' ? 'h-4 w-4' : 'h-10 w-10')}
      data-testid="carrier-logo"
      data-fallback="false"
    />
  );
}
